import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Icon from "@/components/Icon";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ImageSearchModal from "@/components/ImageSearchModal";
import { MapPin, Search, Users, Store } from "lucide-react";
import { useHeatmap } from "@/hooks/useHeatmap";

export default function CitiesPage() {
  const [cities, setCities] = useState([]);
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("");
  const navigate = useNavigate();
  const { heatFor } = useHeatmap();
  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  const search = (e) => {
    e?.preventDefault?.();
    const qs = new URLSearchParams();
    if (query) qs.set("search", query);
    if (city) qs.set("city", city);
    navigate(`/vendors?${qs.toString()}`);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> Maharashtra · India · Abroad
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
            Pick your city
          </h1>
          <p className="text-muted-foreground mt-2">
            {cities.length} cities — Maharashtra, pan-India and select international hubs.
          </p>
        </div>
        <ImageSearchModal city={city} />
      </div>

      <form onSubmit={search} className="mb-8 grid grid-cols-1 sm:grid-cols-[1fr_240px_auto] gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="cities-search-input"
            className="pl-9 h-12 rounded-full"
            placeholder="Search products, services, vendors — e.g. ‘Paithani saree’, ‘2 BHK flat’, ‘Modak’"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select
          value={city}
          onChange={(e) => setCity(e.target.value)}
          data-testid="cities-search-city-select"
          className="h-12 rounded-full border border-input bg-background px-4 text-sm"
        >
          <option value="">All cities</option>
          {cities.map((c) => (<option key={c.name} value={c.name}>{c.name}</option>))}
        </select>
        <Button type="submit" size="lg" className="rounded-full" data-testid="cities-search-submit">
          Search
        </Button>
      </form>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4 stagger">
        {cities.map((c) => {
          const heat = heatFor("city", c.name, c.color);
          return (
            <Link
              key={c.name}
              to={`/categories?city=${encodeURIComponent(c.name)}`}
              data-testid={`city-link-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group rounded-2xl border border-border/60 bg-card p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <div
                className="h-12 w-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                style={{ background: heat.bg, color: heat.fg }}
                title={heat.count > 0
                  ? `${heat.vendors} vendor${heat.vendors === 1 ? "" : "s"} · ${heat.members} member${heat.members === 1 ? "" : "s"}`
                  : "No activity yet"}
              >
                <Icon name={c.icon} className="h-6 w-6" />
              </div>
              <div className="mt-3 font-semibold text-sm sm:text-base leading-tight">{c.name}</div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mt-1">
                {c.region}
              </div>
              {heat.count > 0 && (
                <div className="mt-1.5 flex items-center gap-2 text-[10px] text-muted-foreground" data-testid={`city-heat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="inline-flex items-center gap-0.5" title="Approved vendors here">
                    <Store className="h-3 w-3" /> {heat.vendors}
                  </span>
                  <span className="inline-flex items-center gap-0.5" title="Approved members here">
                    <Users className="h-3 w-3" /> {heat.members}
                  </span>
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
